import { CSSProperties, Fragment, ReactNode } from 'react';
import './IdText.scss';

type IdSystem = 'chargebee' | 'exact';

/**
 * The ids the screens show, and the system each comes from. An Exact number is only recognised after the word that
 * says what it is ("boekstuk 26710317"), because on its own it looks like any other number.
 */
const PATTERN = /((?:boekstuk|bankboeking|post)\s+)([0-9a-f]{6,}|\d{5,})|\b(txn_[A-Za-z0-9]+)\b|\b(CN-[A-Za-z0-9-]+)\b|\b(20\d{7})\b/g;

/** A colour of its own for every id, worked out from the id: the same id has the same colour wherever it shows. */
const hueOf = (id: string): number => {
    let hash = 0;

    for (let i = 0; i < id.length; i++) {
        hash = (hash * 31 + id.charCodeAt(i)) | 0;
    }

    return Math.abs(hash) % 360;
};

export const IdChip = ({ id, system }: { id: string; system: IdSystem }) => {
    const hue = hueOf(id);
    const style = { '--id-hue': hue } as CSSProperties;

    return (
        <span className={`id-chip id-chip--${system}`} style={style} title={system === 'exact' ? `Exact: ${id}` : `Chargebee: ${id}`}>
            {id}
        </span>
    );
};

/** A text with every id in it as a coloured chip, so the same payment or entry is spotted at a glance. */
export const IdText = ({ text }: { text?: string | null }) => {
    if (!text) {
        return null;
    }

    const parts: ReactNode[] = [];
    let last = 0;

    for (const match of text.matchAll(PATTERN)) {
        const at = match.index ?? 0;

        parts.push(text.slice(last, at));

        if (match[2]) {
            parts.push(match[1]);
            parts.push(<IdChip key={at} id={match[2]} system="exact" />);
        } else {
            parts.push(<IdChip key={at} id={match[3] ?? match[4] ?? match[5]} system="chargebee" />);
        }

        last = at + match[0].length;
    }

    parts.push(text.slice(last));

    return (
        <>
            {parts.map((part, index) => (
                <Fragment key={index}>{part}</Fragment>
            ))}
        </>
    );
};
